// ============================================
// API CONFIGURATION
// ============================================

export const API_CONFIG = {
  baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:7000/api/v1',
  timeout: 30000,
};

export const ENDPOINTS = {
  // Auth
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    refresh: '/auth/refresh',
    logout: '/auth/logout',
    me: '/auth/me',
  },
  // Tenants
  tenants: {
    list: '/tenants',
    create: '/tenants',
    get: (id: string) => `/tenants/${id}`,
    update: (id: string) => `/tenants/${id}`,
    delete: (id: string) => `/tenants/${id}`,
  },
  // Users
  users: {
    list: '/users',
    create: '/users',
    get: (id: string) => `/users/${id}`,
    update: (id: string) => `/users/${id}`,
    delete: (id: string) => `/users/${id}`,
    resetPassword: (id: string) => `/users/${id}/reset-password`,
    changePassword: (id: string) => `/users/${id}/password`,
    // Profile
    profile: '/users/me/profile',
    updateProfile: '/users/me/profile',
  },
  // Accounts
  accounts: {
    list: '/accounts',
    create: '/accounts',
    get: (id: string) => `/accounts/${id}`,
    delete: (id: string) => `/accounts/${id}`,
    bolsao: '/accounts/bolsao',
    virtual: '/accounts/virtual',
    real: '/accounts/real',
    balance: (id: string) => `/accounts/${id}/balance`,
    // Balance Management by Profile
    summary: '/accounts/summary',
    myAccount: '/accounts/my-account',
    balanceSecure: (id: string) => `/accounts/${id}/balance-secure`,
    children: (id: string) => `/accounts/${id}/children`,
  },
  // Transactions
  transactions: {
    list: '/transactions',
    get: (id: string) => `/transactions/${id}`,
    pix: {
      charge: '/transactions/pix/charge',
      send: '/transactions/pix/send',
      schedule: '/transactions/pix/schedule',
    },
    transfer: '/transactions/transfer',
  },
  // Statements
  statements: {
    list: '/statements',
    report: '/statements/report',
    export: '/statements/export',
    balances: '/statements/balances',
    fees: '/statements/fees',
    // Statement by Profile
    myStatement: '/statements/my-statement',
    runningBalance: (accountId: string) => `/statements/${accountId}/running-balance`,
    secure: (accountId: string) => `/statements/${accountId}/secure`,
    byAccountType: (type: string) => `/statements/by-account-type/${type}`,
  },
  // Admin
  admin: {
    providers: (tenantId: string) => `/admin/tenants/${tenantId}/providers`,
    testProvider: (tenantId: string, provider: string) => 
      `/admin/tenants/${tenantId}/providers/${provider}/test`,
    webhook: (tenantId: string) => `/admin/tenants/${tenantId}/webhook`,
    summary: '/admin/tenants/summary',
  },
  // Pix Config
  pixConfig: {
    createKey: '/pix-config/keys',
    getConfig: (accountId: string) => `/pix-config/accounts/${accountId}`,
    updateConfig: (accountId: string) => `/pix-config/accounts/${accountId}`,
    deleteKey: (accountId: string) => `/pix-config/keys/${accountId}`,
    overview: '/pix-config/overview',
    pendingSetup: '/pix-config/accounts/pending-setup',
  },
  // Split
  split: {
    rules: '/split/rules',
    getRule: (id: string) => `/split/rules/${id}`,
  },
  // Boleto
  boleto: {
    create: '/boleto',
    get: (id: string) => `/boleto/${id}`,
    list: '/boleto',
  },
  // Card
  card: {
    payment: '/card/payment',
    get: (id: string) => `/card/payment/${id}`,
    refund: (id: string) => `/card/payment/${id}/refund`,
  },
  // Audit
  audit: {
    logs: '/audit/logs',
    get: (id: string) => `/audit/logs/${id}`,
    export: '/audit/export',
  },
  // Webhooks
  webhooks: {
    openpix: '/webhooks/openpix',
    iugu: '/webhooks/iugu',
    asaas: '/webhooks/asaas',
  },
  // Roles Management
  roles: {
    assign: '/admin/roles',
    revoke: (id: string) => `/admin/roles/${id}`,
    userRoles: (userId: string) => `/admin/roles/user/${userId}`,
    tenantManagers: (tenantId: string) => `/admin/roles/tenants/${tenantId}/managers`,
    checkPermission: '/admin/roles/check-permission',
    highestRole: '/admin/roles/highest-role',
  },
  // Costs Management (Admin)
  costs: {
    providerCosts: '/admin/costs/providers',
    setProviderCost: (providerId: string) => `/admin/costs/providers/${providerId}/costs`,
    getProviderCosts: (providerId: string) => `/admin/costs/providers/${providerId}/costs`,
    partners: '/admin/costs/partners',
    createPartner: '/admin/costs/partners',
    getPartner: (partnerId: string) => `/admin/costs/partners/${partnerId}`,
    partnerCommissions: (partnerId: string) => `/admin/costs/partners/${partnerId}/commissions`,
    linkCommission: (partnerId: string) => `/admin/costs/partners/${partnerId}/commissions`,
    calculateCost: '/admin/costs/breakdown',
    breakdown: '/admin/costs/breakdown',
    // Fee configurations (admin listing)
    feeConfigurations: '/admin/costs/fee-configurations',
    getFeeConfiguration: (id: string) => `/admin/costs/fee-configurations/${id}`,
    // Fee validation
    validateFee: '/admin/costs/fees/validate',
    feesAtRisk: '/admin/costs/fees/at-risk',
    checkFeesAfterCostChange: '/admin/costs/fees/check-after-cost-change',
  },
  // Limits Management
  limits: {
    set: '/limits',
    get: (userId: string, operationType: string) => `/limits/${userId}/${operationType}`,
    effective: (userId: string, operationType: string) => `/limits/${userId}/${operationType}/effective`,
    consumption: (userId: string, operationType: string) => `/limits/${userId}/${operationType}/consumption`,
    check: '/limits/check',
    // Manager endpoints
    userLimits: (userId: string) => `/manager/users/${userId}/limits`,
    setUserLimit: (userId: string) => `/manager/users/${userId}/limits`,
    // User endpoints
    myLimits: '/user/limits',
  },
  // Approvals Management
  approvals: {
    // Manager endpoints
    pending: '/manager/approvals/pending',
    approve: (id: string) => `/manager/approvals/${id}/approve`,
    reject: (id: string) => `/manager/approvals/${id}/reject`,
    history: '/manager/approvals/history',
    // User endpoints
    myApprovals: '/user/approvals',
    // Admin endpoints
    all: '/approvals',
  },
  // Manager Commissions
  managerCommissions: {
    config: '/costs/manager-commissions/config',
    setConfig: '/costs/manager-commissions/config',
    myCommissions: '/manager/commissions',
    summary: '/manager/commissions/summary',
    markPaid: '/costs/manager-commissions/mark-paid',
  },
  // Partner Commissions Report
  partnerCommissions: {
    report: '/costs/partner-commissions/report',
    byPartner: (partnerId: string) => `/costs/partner-commissions/partner/${partnerId}`,
  },
  // Reconciliation
  reconciliation: {
    report: '/reconciliation/report',
    discrepancies: '/reconciliation/discrepancies',
    unreconciled: '/reconciliation/unreconciled',
    reconcile: (operationId: string) => `/reconciliation/${operationId}`,
  },
  // Cost Audit
  costAudit: {
    history: (entityType: string, entityId: string) => `/costs/audit/${entityType}/${entityId}`,
    byPeriod: '/costs/audit/by-period',
    impact: (auditLogId: string) => `/costs/audit/${auditLogId}/impact`,
  },
  // Admin Dashboard (Roles/Fees)
  adminDashboard: {
    revenue: '/admin/dashboard/revenue',
    margins: '/admin/dashboard/margins',
    reconciliation: '/admin/dashboard/reconciliation-status',
    feesAtRisk: '/admin/costs/fees/at-risk',
    tenantsSummary: '/admin/dashboard/tenants-summary',
    partnersSummary: '/admin/dashboard/partners-summary',
    alerts: '/admin/dashboard/alerts',
  },
  // Notifications
  notifications: {
    list: '/notifications',
    unreadCount: '/notifications/unread-count',
    markRead: (id: string) => `/notifications/${id}/read`,
    markAllRead: '/notifications/read-all',
  },
  // Manager Users
  managerUsers: {
    list: '/manager/users',
    create: '/manager/users',
    get: (id: string) => `/manager/users/${id}`,
    update: (id: string) => `/manager/users/${id}`,
  },
  // Entities
  entities: {
    list: '/entities',
    create: '/entities',
    get: (id: string) => `/entities/${id}`,
    update: (id: string) => `/entities/${id}`,
    delete: (id: string) => `/entities/${id}`,
    accounts: (id: string) => `/entities/${id}/accounts`,
    // Hierarchy endpoints
    tree: '/entities/tree',
    root: '/entities/root',
    children: (id: string) => `/entities/${id}/children`,
  },
  // Bank Connections
  bankConnections: {
    list: '/bank-connections',
    create: '/bank-connections',
    get: (id: string) => `/bank-connections/${id}`,
    update: (id: string) => `/bank-connections/${id}`,
    delete: (id: string) => `/bank-connections/${id}`,
    test: (id: string) => `/bank-connections/${id}/test`,
    setDefault: (id: string) => `/bank-connections/${id}/default`,
    default: '/bank-connections/default',
  },
  // Fee Rules
  feeRules: {
    list: '/fee-rules',
    create: '/fee-rules',
    get: (id: string) => `/fee-rules/${id}`,
    update: (id: string) => `/fee-rules/${id}`,
    delete: (id: string) => `/fee-rules/${id}`,
  },
  // Provider Engine
  providerEngine: {
    // Templates
    templates: '/provider-templates',
    templateById: (id: string) => `/provider-templates/${id}`,
    cloneTemplate: (id: string) => `/provider-templates/${id}/clone`,
    exportTemplate: (id: string) => `/provider-templates/${id}/export`,
    importTemplate: '/provider-templates/import',
    // Instances
    instances: '/provider-instances',
    instanceById: (id: string) => `/provider-instances/${id}`,
    activateInstance: (id: string) => `/provider-instances/${id}/activate`,
    suspendInstance: (id: string) => `/provider-instances/${id}/suspend`,
    testInstance: (id: string) => `/provider-instances/${id}/test`,
    // Monitoring
    monitoring: '/monitoring',
    // Crypto Wallets
    cryptoWallets: '/crypto-wallets',
    cryptoWalletById: (id: string) => `/crypto-wallets/${id}`,
    cryptoWalletBalance: (id: string) => `/crypto-wallets/${id}/balance`,
    cryptoWalletsByAccount: (accountId: string) => `/crypto-wallets/account/${accountId}`,
    // Swaps
    swaps: '/swaps',
    swapQuote: '/swaps/quote',
    swapExecute: '/swaps/execute',
    swapHistory: (walletId: string) => `/swaps/history/${walletId}`,
    // Workflows
    workflows: '/workflows',
    workflowById: (id: string) => `/workflows/${id}`,
    activateWorkflow: (id: string) => `/workflows/${id}/activate`,
    deactivateWorkflow: (id: string) => `/workflows/${id}/deactivate`,
    executeWorkflow: (id: string) => `/workflows/${id}/execute`,
    workflowExecutions: (id: string) => `/workflows/${id}/executions`,
    // Notifications
    notificationsSend: '/notifications/send',
    notificationsSendBulk: '/notifications/send-bulk',
    alertRules: '/notifications/alert-rules',
    alertRuleById: (id: string) => `/notifications/alert-rules/${id}`,
    enableAlertRule: (id: string) => `/notifications/alert-rules/${id}/enable`,
    disableAlertRule: (id: string) => `/notifications/alert-rules/${id}/disable`,
    // Reports
    reports: '/reports',
    reportTransactions: '/reports/transactions',
    reportFees: '/reports/fees',
    reportBalance: '/reports/balance',
    reportReconciliation: '/reports/reconciliation',
    reportCompliance: '/reports/compliance',
    reportExport: '/reports/export',
    reportFormats: '/reports/export/formats',
    reportTypes: '/reports/types',
    // Documentation
    docsOpenApi: (templateId: string) => `/docs/templates/${templateId}/openapi`,
    docsOperations: (templateId: string) => `/docs/templates/${templateId}/operations`,
    docsMarkdown: (templateId: string) => `/docs/templates/${templateId}/markdown`,
  },
  // Virtual Accounts
  virtualAccounts: {
    list: '/virtual-accounts',
    create: '/virtual-accounts',
    stats: '/virtual-accounts/stats',
    get: (id: string) => `/virtual-accounts/${id}`,
    balance: (id: string) => `/virtual-accounts/${id}/balance`,
    statement: (id: string) => `/virtual-accounts/${id}/statement`,
    transfer: (id: string) => `/virtual-accounts/${id}/transfer`,
    updatePixKey: (id: string) => `/virtual-accounts/${id}/pix-key`,
    withdraw: (id: string) => `/virtual-accounts/${id}/withdraw`,
    pixCharge: (id: string) => `/virtual-accounts/${id}/pix/charge`,
    pixChargeStatus: (id: string, chargeId: string) => `/virtual-accounts/${id}/pix/charge/${chargeId}`,
  },
  // Crypto Operations (Virtual Account based)
  crypto: {
    // Supported assets and networks
    supportedCryptos: (vaId: string) => `/virtual-accounts/${vaId}/crypto/supported-cryptos`,
    networks: (vaId: string) => `/virtual-accounts/${vaId}/crypto/networks`,
    // Wallets
    wallets: (vaId: string) => `/virtual-accounts/${vaId}/crypto/wallets`,
    walletById: (vaId: string, walletId: string) => `/virtual-accounts/${vaId}/crypto/wallets/${walletId}`,
    registerWallet: (vaId: string) => `/virtual-accounts/${vaId}/crypto/wallets`,
    // Quotations
    quotation: (vaId: string) => `/virtual-accounts/${vaId}/crypto/quotation`,
    quotationsPublic: '/crypto/quotations',
    // Purchase
    purchase: (vaId: string) => `/virtual-accounts/${vaId}/crypto/purchase`,
    purchases: (vaId: string) => `/virtual-accounts/${vaId}/crypto/purchases`,
    purchaseById: (vaId: string, purchaseId: string) => `/virtual-accounts/${vaId}/crypto/purchases/${purchaseId}`,
    // Sale
    createSale: (vaId: string) => `/virtual-accounts/${vaId}/crypto/sales`,
    sales: (vaId: string) => `/virtual-accounts/${vaId}/crypto/sales`,
    saleById: (vaId: string, saleId: string) => `/virtual-accounts/${vaId}/crypto/sales/${saleId}`,
    acceptSale: (vaId: string) => `/virtual-accounts/${vaId}/crypto/sales/accept`,
    confirmSale: (vaId: string, saleId: string) => `/virtual-accounts/${vaId}/crypto/sales/${saleId}/confirm`,
    cancelSale: (vaId: string, saleId: string) => `/virtual-accounts/${vaId}/crypto/sales/${saleId}/cancel`,
    // P2P Marketplace
    marketplace: '/virtual-accounts/marketplace/sales',
    // Pools (DeFi) - via virtual account
    pools: (vaId: string) => `/virtual-accounts/${vaId}/crypto/pools`,
    poolById: (vaId: string, poolId: string) => `/virtual-accounts/${vaId}/crypto/pools/${poolId}`,
    poolDeposit: (vaId: string) => `/virtual-accounts/${vaId}/crypto/pools/deposit`,
    poolWithdraw: (vaId: string) => `/virtual-accounts/${vaId}/crypto/pools/withdraw`,
    positions: (vaId: string) => `/virtual-accounts/${vaId}/crypto/positions`,
    positionById: (vaId: string, positionId: string) => `/virtual-accounts/${vaId}/crypto/positions/${positionId}`,
  },
  // DeFi Pools (Public endpoints)
  defiPools: {
    list: '/defi-pools',
    get: (poolId: string) => `/defi-pools/${poolId}`,
    positions: '/defi-pools/positions',
    positionById: (positionId: string) => `/defi-pools/positions/${positionId}`,
    deposit: (poolId: string) => `/defi-pools/${poolId}/deposit`,
    withdraw: (positionId: string) => `/defi-pools/positions/${positionId}/withdraw`,
  },
  // Compliance
  compliance: {
    pnl: '/compliance/pnl',
    audit: '/compliance/audit',
    export: '/compliance/export',
  },
  // Multi-Tenant Fee Cascade
  feeCascade: {
    // Platform Fees (Super Admin)
    platformFees: {
      list: '/super-admin/platform-fees',
      create: '/super-admin/platform-fees',
      get: (id: string) => `/super-admin/platform-fees/${id}`,
      getByOperation: (operationType: string) => `/super-admin/platform-fees/operation/${operationType}`,
      update: (id: string) => `/super-admin/platform-fees/${id}`,
      delete: (id: string) => `/super-admin/platform-fees/${id}`,
      calculate: '/super-admin/platform-fees/calculate',
    },
    // Tenant Fees (Tenant Admin)
    tenantFees: {
      list: '/admin/tenant-fees',
      create: '/admin/tenant-fees',
      get: (id: string) => `/admin/tenant-fees/${id}`,
      getByOperation: (operationType: string) => `/admin/tenant-fees/operation/${operationType}`,
      update: (id: string) => `/admin/tenant-fees/${id}`,
      delete: (id: string) => `/admin/tenant-fees/${id}`,
      calculate: '/admin/tenant-fees/calculate',
      simulateMargin: '/admin/tenant-fees/simulate-margin',
      platformMinimums: '/admin/tenant-fees/platform-minimums',
    },
    // Cascade Calculator
    cascade: {
      calculate: '/fee-cascade/calculate',
      validate: '/fee-cascade/validate',
      simulate: '/fee-cascade/simulate',
    },
  },
  // Revenue Tracking
  revenue: {
    entries: '/revenue/entries',
    platformSummary: '/revenue/summary/platform',
    tenantSummary: '/revenue/summary/tenant',
    accumulations: '/revenue/accumulations',
    dailyTrend: '/revenue/trend/daily',
    monthlyTrend: '/revenue/trend/monthly',
  },
  // Fee Health Check (Super Admin)
  feeHealth: {
    check: '/super-admin/fee-health/check',
    runCheck: '/super-admin/fee-health/check',
    alerts: '/super-admin/fee-health/alerts',
    resolveAlert: (id: string) => `/super-admin/fee-health/alerts/${id}/resolve`,
    recommendations: (tenantId: string, operationType: string) => `/super-admin/fee-health/recommendations/${tenantId}/${operationType}`,
    summary: '/super-admin/fee-health/summary',
    checkTenant: (tenantId: string) => `/super-admin/fee-health/check/tenant/${tenantId}`,
    checkOperation: (operationType: string) => `/super-admin/fee-health/check/operation/${operationType}`,
  },
  // Fee Config Audit
  feeAudit: {
    history: '/fee-audit/history',
    byEntity: (entityType: string, entityId: string) => `/fee-audit/${entityType}/${entityId}`,
    export: '/fee-audit/export',
  },
  // Fee Reconciliation
  feeReconciliation: {
    report: '/fee-reconciliation/report',
    discrepancies: '/fee-reconciliation/discrepancies',
    reconcile: (id: string) => `/fee-reconciliation/${id}/reconcile`,
    override: (id: string) => `/fee-reconciliation/${id}/override`,
  },
  // Banking (Correntista)
  banking: {
    myAccounts: '/banking/my-accounts',
    myBalance: '/banking/my-balance',
    statement: '/banking/statement',
    pixCharge: '/banking/pix/charge',
    pixSend: '/banking/pix/send',
    transfer: '/banking/transfer',
    withdraw: '/banking/withdraw',
  },
  // AnyPayBR (Admin - Master Balance Provider)
  anypay: {
    balance: '/admin/anypay/balance',
    transactions: '/admin/anypay/transactions',
    payments: '/admin/anypay/payments',
    withdrawals: '/admin/anypay/withdrawals',
    testConnection: '/admin/anypay/test-connection',
    withdraw: '/admin/anypay/withdraw',
    sync: '/admin/anypay/sync',
  },
  // Tenant Fee Config (Provider Engine - AnyPayBR)
  tenantFeeConfig: {
    get: (tenantId: string) => `/provider-engine/tenant-fee-config/${tenantId}`,
    update: (tenantId: string) => `/provider-engine/tenant-fee-config/${tenantId}`,
    calculate: '/provider-engine/tenant-fee-config/calculate',
  },
  // Facial Verification Rules
  facialVerification: {
    rules: '/facial-verification/rules',
    ruleById: (id: string) => `/facial-verification/rules/${id}`,
    enableRule: (id: string) => `/facial-verification/rules/${id}/enable`,
    disableRule: (id: string) => `/facial-verification/rules/${id}/disable`,
    auditLogs: '/facial-verification/audit-logs',
    bypassTokens: '/facial-verification/bypass-tokens',
  },
  // Benefits (Benefícios)
  benefits: {
    // Banking (Correntista)
    catalog: '/banking/benefits',
    categories: '/banking/benefits/categories',
    planDetails: (planId: string) => `/banking/benefits/${planId}`,
    mySubscriptions: '/banking/benefits/my-subscriptions',
    subscribe: '/banking/benefits/my-subscriptions',
    subscriptionDetails: (id: string) => `/banking/benefits/my-subscriptions/${id}`,
    cancelSubscription: (id: string) => `/banking/benefits/my-subscriptions/${id}`,
    addDependent: (id: string) => `/banking/benefits/my-subscriptions/${id}/dependents`,
    removeDependent: (id: string, depId: string) => `/banking/benefits/my-subscriptions/${id}/dependents/${depId}`,
    getCard: (id: string) => `/banking/benefits/my-subscriptions/${id}/card`,
    reissueCard: (id: string) => `/banking/benefits/my-subscriptions/${id}/card/reissue`,
    // Admin
    adminTemplates: '/admin/benefits/templates',
    adminPlans: '/admin/benefits/plans',
    adminPlanCreate: '/admin/benefits/plans',
    adminPlanDetails: (id: string) => `/admin/benefits/plans/${id}`,
    adminPlanUpdate: (id: string) => `/admin/benefits/plans/${id}`,
    adminSubscriptions: '/admin/benefits/subscriptions',
    adminSubscriptionDetails: (id: string) => `/admin/benefits/subscriptions/${id}`,
    adminApprove: (id: string) => `/admin/benefits/subscriptions/${id}/approve`,
    adminReject: (id: string) => `/admin/benefits/subscriptions/${id}/reject`,
    adminDashboard: '/admin/benefits/dashboard',
    adminPayroll: '/admin/benefits/payroll',
    adminPayrollGenerate: '/admin/benefits/payroll/generate',
    adminPayrollProcess: '/admin/benefits/payroll/process',
  },
};


// Alias for backward compatibility
export const API_ENDPOINTS = ENDPOINTS;
